import { create } from "apisauce";
import { cacheImg, getCachedImg } from "./utils";

export const createApi = () => {
  const api = create({
    baseURL: import.meta.env.VITE_URL_API,
    headers: {
      "Cache-Control": "no-cache",
    },
    timeout: 300000,
    withCredentials: true,
  });

  // Security
  const login = (credentialResponse) => {
    return api.post(`/api/auth/login`, credentialResponse);
  };

  const logout = () => {
    return api.post(`/api/auth/logout`);
  };

  const validateToken = () => {
    return api.get(`/api/auth/validateToken`, {});
  };

  // User
  const getUsers = () => {
    return api.get(`/api/users`);
  };

  const getUserNames = () => {
    return api.get(`/api/users/names`);
  };

  const getUserEmployeeInfo = (hrCode) => {
    return api.get(`/api/users/${hrCode}/employee`);
  };

  const getUserActionLogs = (userId) => {
    return api.get(`/api/users/${userId}/action-logs`);
  };

  const saveUser = (user) => {
    return api.post(`/api/users/${user.id}`, user);
  };

  const createUser = (user) => {
    return api.post(`/api/users`, user);
  };

  const exportUsers = () => {
    return api.get(`/api/users/export`);
  };

  const savePinnedTabs = (tabs) => api.post(`/api/users/pinnedTabs`, { pinnedTabs: tabs });

  const saveVisibility = (visibility) => {
    return api.post(`/api/users/visibility`, visibility);
  };

  // Roles
  const getRoles = () => {
    return api.get(`/api/roles`);
  };

  const getRolesWithCount = () => {
    return api.get(`/api/roles/with-count`);
  };

  const editRole = (role) => {
    return api.post(`/api/roles/${role.id}`, role);
  };

  // Employee
  const getEmployees = () => {
    return api.get(`/api/employees`);
  };

  const getEmployeeByHrCode = (hrCode) => {
    return api.get(`/api/employees/hr/${hrCode}`);
  };

  const getEmployeeNames = () => {
    return api.get(`/api/employees/names`);
  };

  const getPersonalProfile = () => {
    return api.get(`/api/employees/my-profile`);
  };

  const saveEmployeeProfile = (employeeId, hrCode, data) => {
    return api.post(`/api/employees/${employeeId}/profile`, { ...data, hrCode });
  };

  // Profile Image
  const uploadProfileImg = (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/api/profile-img/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  const getProfileImg = (hrCode) => {
    return api.get(`/api/profile-img/view/${hrCode}`, {}, { responseType: "blob" });
  };

  const getProfileImgThumb = (hrCode) => {
    let cached = getCachedImg(hrCode);
    if (cached) {
      return Promise.resolve({ status: 200, data: cached });
    }
    let response = api.get(`/api/profile-img/view-thumb/${hrCode}`, {}, { responseType: "blob" });
    response.then((res) => {
      if (res.status === 200) {
        cacheImg(hrCode, res.data);
      } else {
        cacheImg(hrCode, null);
      }
    });
    return response;
  };

  const getLatestPerformance = () => {
    return api.get(`/api/employees/hr/latest-performance`);
  };

  // Performance Acknowledgement
  const acknowledgePerformance = (performanceId) => {
    return api.post(`/api/employees/performance/${performanceId}/acknowledge`);
  };

  // Dictionary Value
  const getDictionaryValuesGrouped = () => {
    return api.get(`/api/dictionary/types/values`);
  };

  // Structure - Division
  const getDivisionsStructure = () => {
    return api.get(`/api/teams/divisions`);
  };

  // Structure - Department
  const getDepartmentsStructure = (divisionId) => {
    if (divisionId) {
      return api.get(`/api/teams/departments?divisionId=${divisionId}`);
    }
    return api.get(`/api/teams/departments`);
  };

  // Structure - Team
  const getTeamsStructure = (departmentId) => {
    if (departmentId) {
      return api.get(`/api/teams/teams?departmentId=${departmentId}`);
    }
    return api.get(`/api/teams/teams`);
  };

  // Executive Leadership
  const getExecutiveLeadership = () => api.get(`/api/executive-leadership`);
  const saveExecutiveLeadership = (members) => api.post(`/api/executive-leadership`, { members });

  return {
    login,
    logout,

    // User
    validateToken,
    getUsers,
    getUserNames,
    getUserEmployeeInfo,
    saveUser,
    createUser,
    exportUsers,
    getUserActionLogs,
    savePinnedTabs,
    saveVisibility,

    // Roles
    getRoles,
    getRolesWithCount,
    editRole,

    // Employee
    getEmployees,
    getEmployeeByHrCode,
    getEmployeeNames,
    getPersonalProfile,
    saveEmployeeProfile,

    // Profile Image
    uploadProfileImg,
    getProfileImg,
    getProfileImgThumb,
    getLatestPerformance,

    // Performance Acknowledgement
    acknowledgePerformance,

    // Dictionary Value
    getDictionaryValuesGrouped,

    // Structure
    getDivisionsStructure,
    getDepartmentsStructure,
    getTeamsStructure,

    // Executive Leadership
    getExecutiveLeadership,
    saveExecutiveLeadership,
  };
};

// Creates a singleton
export default createApi();
